import cn from 'classnames';
import React, { useEffect, useMemo } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import useSWR from 'swr';

import { Link, Spinner } from 'components';
import { ButtonV3, InputV2 } from 'components/ComponentV2';
import { Form } from 'components/form';
import SelectionTickMark from 'components/form/SelectionTickMark';
import { Col, Row } from 'components/layout';

import useDebounce from 'hooks/useDebounce';
import { API_ROUTES } from 'lib/api-routes';
import {
  signUpSocietyState,
  SocietyAttributes,
} from 'lib/atoms/signUpSocietyAtom';
import {
  StudentSocietiesResponse,
  StudentSocietyAttributes,
} from 'lib/models/student-society';
import {
  UniversitiesResponse,
  UniversityAttributes,
} from 'lib/models/university';
import { PAGE_ROUTES } from 'lib/page-routes';
import { get } from 'lib/utils/http';
import { parseArrayResponse } from 'lib/utils/parser';
import { REGEX } from 'lib/utils/regex';
import { PRIVACY_POLICY, TERMS_OF_USE } from 'utils/urls';

import StepHeading from '../Steps/StepHeading';
import styles from './UniversityAndSociety.module.scss';

export interface UniversityAndSocietyProps {
  className: string;
  setStep: (step: number) => void;
}

export const UniversityAndSociety: React.FC<UniversityAndSocietyProps> = ({
  className,
  setStep,
}: UniversityAndSocietyProps) => {
  const [societyState, setSocietyState] = useRecoilState(signUpSocietyState);
  const { society } = societyState;
  const [t] = useTranslation('signup.manager');

  const contentClassNames = cn([styles.container], className);

  const [showDropDownUniversity, setShowDropDownUniversity] =
    React.useState<boolean>(false);

  const [showDropDownSociety, setShowDropDownSociety] =
    React.useState<boolean>(false);

  const [UniversityNotOnList, setUniversityNotOnList] = React.useState<boolean>(
    !!society?.isNewUniversity
  );
  const [societyNotOnList, setSocietyNotOnList] = React.useState<boolean>(
    !!society?.isNewSociety
  );

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
    watch,
    setFocus,
  } = useForm({
    defaultValues: {
      university_name: society?.university_name || '',
      university_id: society?.isNewUniversity ? '' : society?.university_id,
      university_website: society?.university_website,
      name: society?.name || '',
      id: society?.id || '',
    },
    mode: 'onBlur',
  });

  const university_name = watch('university_name');
  const name = watch('name');
  const university_id = watch('university_id');

  useEffect(() => {
    if (!university_name) {
      setFocus('university_name');
    } else {
      if (!name) setFocus('name');
    }
  }, [setFocus]);

  const debouncedSearchUniversity = useDebounce(university_name.trim(), 250);
  const { data, isLoading } = useSWR<UniversitiesResponse>(
    `${API_ROUTES.UNIVERSITIES}?q=${debouncedSearchUniversity}&per_page=100&with_icon=true`,
    get
  );

  const debouncedSearchSociety = useDebounce(name?.trim(), 250);
  const shouldApplySimilarSpelled =
    societyNotOnList && name?.trim()?.length > 2;

  const { data: societiesData, isLoading: societiesLoading } =
    useSWR<StudentSocietiesResponse>(
      `${API_ROUTES.STUDENT_SOCIETIES}?q=${
        societyNotOnList ? '' : debouncedSearchSociety
      }&similarly_spelled=${
        shouldApplySimilarSpelled ? debouncedSearchSociety : ''
      }&university_ids[]=${university_id}&per_page=100&with_icon=true`,
      get
    );

  const universities = useMemo(() => {
    const list = parseArrayResponse<UniversityAttributes>(data);
    return list.map(({ id, name, icon_url }) => ({
      id,
      value: id,
      label: name,
      avatar: icon_url,
    }));
  }, [data]);

  const societies = useMemo(() => {
    const list = parseArrayResponse<StudentSocietyAttributes>(societiesData);
    return list.map(({ id, name, icon_url }) => ({
      id,
      value: id,
      label: name,
      avatar: icon_url,
    }));
  }, [societiesData]);

  const onSubmit: SubmitHandler<SocietyAttributes> = async (input) => {
    const data = {
      ...input,
      isNewUniversity: UniversityNotOnList,
      isNewSociety: societyNotOnList,
    };
    setSocietyState({ ...societyState, society: data, step: 1 });
    setStep(1);
  };

  return (
    <section className={contentClassNames}>
      <StepHeading
        title={t('university-and-society.header.title')}
        desc={
          societyNotOnList
            ? t('university-and-society.header.desc')
            : t('university-and-society.header.desc-2')
        }
      />
      <Form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
        <Row className={styles.row}>
          <Col span={24} className={styles.col}>
            <input type="hidden" {...register('university_id')} />
            <InputV2
              type="text"
              id="company-name"
              label={
                UniversityNotOnList
                  ? t('university-and-society.university-name')
                  : t('university-and-society.university')
              }
              placeholder={t('university-and-society.university-placeholder')}
              {...register('university_name', {
                required: 'Required',
              })}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
                setShowDropDownUniversity(true);
                setValue('university_id', '');
              }}
            />
            {showDropDownUniversity && university_name && (
              <div className={styles.dropdownList}>
                {(isLoading || universities.length > 0) && (
                  <div className={styles.dropdownListWrapper}>
                    {isLoading && (
                      <div className={styles.dropdownListItem}>
                        <Spinner size="small" />
                      </div>
                    )}
                    {universities?.map((option, index) => (
                      <div
                        key={index}
                        className={styles.dropdownListItem}
                        onClick={() => {
                          setValue('university_id', option.id);
                          setValue('university_name', option.label);
                          setValue('university_website', '');
                          setShowDropDownUniversity(false);
                          setUniversityNotOnList(false);
                          setValue('id', '');
                          setValue('name', '');
                        }}
                      >
                        <img
                          src={option.avatar}
                          alt={option.label}
                          className={styles.dropdownListItemAvatar}
                        />
                        <div
                          className={styles.dropdownListItemText}
                          key={option.id}
                        >
                          {option.label}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div
                  className={cn(styles.dropdownListWrapper, {
                    [styles.dropdownListWrapperBorder]: !!universities?.length,
                  })}
                >
                  {!isLoading && (
                    <div className={styles.dropdownListItem}>
                      <div
                        className={cn(
                          styles.dropdownListItemText,
                          styles.dropdownListItemTextNotOnList
                        )}
                        onClick={() => {
                          setShowDropDownUniversity(false);
                          setUniversityNotOnList(true);
                          setSocietyNotOnList(true);
                          setValue('id', '');
                          setValue('name', '');
                        }}
                      >
                        {t('university-and-society.university-not-on-list')}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </Col>
          {UniversityNotOnList && (
            <Col span={24} className={styles.marginTop}>
              <InputV2
                type="text"
                label={t('university-and-society.university-website')}
                {...register('university_website', {
                  pattern: {
                    value: REGEX.URL,
                    message: t('university-and-society.invalid-website'),
                  },
                })}
                isInvalid={!!errors.university_website}
                labelClassName={styles.fieldLabel}
                placeholder={t(
                  'university-and-society.university-website-placeholder'
                )}
                showOptionalLabel
              />
              {errors.university_website?.message && (
                <div className={styles.errorMessage}>
                  {errors.university_website?.message}
                </div>
              )}
            </Col>
          )}

          <Col span={24} className={cn(styles.marginTop, styles.col)}>
            <input type="hidden" {...register('id')} />
            <InputV2
              type="text"
              id="society-name"
              label={
                societyNotOnList
                  ? t('university-and-society.society-name')
                  : t('university-and-society.society')
              }
              placeholder={t('university-and-society.society-placeholder')}
              {...register('name', {
                required: 'Required',
              })}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
                if (university_id) {
                  setShowDropDownSociety(true);
                  setValue('id', '');
                }
              }}
              disabled={!university_name}
            />
            {showDropDownSociety && name && (
              <div className={styles.dropdownList}>
                {(societiesLoading || societies.length > 0) && (
                  <div className={styles.dropdownListWrapper}>
                    {societiesLoading && (
                      <div className={styles.dropdownListItem}>
                        <Spinner size="small" />
                      </div>
                    )}
                    {societies?.map((option, index) => (
                      <div
                        key={index}
                        className={styles.dropdownListItem}
                        onClick={() => {
                          setValue('id', option.id);
                          setValue('name', option.label);
                          setShowDropDownSociety(false);
                          setSocietyNotOnList(false);
                        }}
                      >
                        <img
                          src={option.avatar}
                          alt={option.label}
                          className={styles.dropdownListItemAvatar}
                        />
                        <div
                          className={styles.dropdownListItemText}
                          key={option.id}
                        >
                          {option.label}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div
                  className={cn(styles.dropdownListWrapper, {
                    [styles.dropdownListWrapperBorder]: !!societies?.length,
                  })}
                >
                  {societiesLoading ? null : (
                    <div className={styles.dropdownListItem}>
                      <div
                        className={cn(
                          styles.dropdownListItemText,
                          styles.dropdownListItemTextNotOnList
                        )}
                        onClick={() => {
                          setShowDropDownSociety(false);
                          setSocietyNotOnList(true);
                        }}
                      >
                        {t('university-and-society.society-not-on-list')}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* Duplicate Societies Similarity Checker */}
            {societyNotOnList &&
              !showDropDownSociety &&
              name?.length > 2 &&
              !societiesLoading &&
              societies.length > 0 && (
                <div className={styles.similerSocietyWrapper}>
                  <p className={styles.similerSocietyWrapperText}>
                    🤔 <span>{t('university-and-society.did-you-mean')}</span>
                  </p>
                  <div className={styles.similerSocietyList}>
                    <div className={styles.similerSocietyListWrapper}>
                      {societies?.map((option, index) => (
                        <div
                          key={index}
                          className={styles.similerSocietyListItem}
                          onClick={() => {
                            setValue('id', option.id);
                            setValue('name', option.label);
                            setShowDropDownSociety(false);
                            setSocietyNotOnList(false);
                          }}
                        >
                          <img
                            src={option.avatar}
                            alt={''}
                            className={styles.similerSocietyListItemAvatar}
                          />
                          <SelectionTickMark
                            checked={option.label === name}
                            className={styles.selectionTickmark}
                            tickClassName={styles.tick}
                          >
                            {option.label}
                          </SelectionTickMark>
                          <hr
                            className={styles.selectionTickmarkSeparator}
                          ></hr>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
          </Col>
        </Row>

        {university_name &&
        !showDropDownUniversity &&
        name &&
        !showDropDownSociety ? (
          <ButtonV3
            type="submit"
            className={styles.submitButton}
            isFullWidth
            isLoading={isSubmitting}
            size="xlarge"
          >
            {t('university-and-society.continue')}
          </ButtonV3>
        ) : null}
      </Form>
      <p className={styles.signupLink}>
        {t('university-and-society.already-have-account')}
        <Link href={PAGE_ROUTES.SIGN_IN_SOCIETY} isPrimary>
          {t('university-and-society.sign-in')}
        </Link>
      </p>

      <p className={styles.termsAndPrivacy}>
        {t('university-and-society.agree-text')}
        <br />
        <Link href={TERMS_OF_USE}>
          {t('university-and-society.terms-of-service')}
        </Link>
        {t('university-and-society.and-our')}
        <Link href={PRIVACY_POLICY}>
          {t('university-and-society.privacy-policy')}
        </Link>
      </p>
    </section>
  );
};
